/**
 * TEMPORARILY COPIED FROM @tableau/share-dialog
 */

import { ChipPicker, ChipPickerProps, ListItem } from '@tableau/widgets';
import React from 'react';
import { Cancelable, debounce } from 'underscore';

import * as styles from '../styles/PickerStyle';

export interface AsyncPickerProps<T extends Object> extends

  Pick<ChipPickerProps, Exclude<keyof ChipPickerProps, 'onValueAdd' | 'onValuesChange' | 'listItems' | 'onFilterItems' | 'children'>> {
  /** The list of recipients to show as chips */
  recipients: T[];

  /**
   * Called when a user is selected from the popup list. It has a reference to a new user object, and the index at which it should be added
   * in the recipients list
   */
  onRecipientAdd?: (recipient: T, targetIndex: number) => void;

  /** Called when a recipient is removed or reordered using drag & drop */
  onRecipientsChange?: (recipients: T[]) => void;

  /** A callback to map a recipient to a list item */
  recipientToListItem: (recipient: T) => ListItem;

  /** A callback to map a recipient to a chip */
  recipientToChip: (recipient: T) => JSX.Element;

  /** A callback to get the unique string identifier of a recipient (for example username)*/
  recipientToStringId: (recipient: T) => string;

  /** The debounce time to use before looking up users */
  debounceTimeout: number;

  /** The callback which queries recipient information. Returns a promise of recipients*/
  fetchRecipientsCallback: (searchTerm: string) => Promise<T[]>;

  /** A callback to notify container of filter changes */
  onFilterChanged?: (filter: string) => void;
}

/**
 * The internal state of the recipient picker. Contains a list of recipients to show in the popup and maintains the current search text
 */
export interface AsyncPickerState<T extends Object> {
  recipientsSearchList: T[];
  filterValue: string;
  inflightFetchRecipientCallbacks: Set<CancelablePromise<T[]>>;
}

const scopedTestId = 'recipient-picker';

export class AsyncPicker<T extends Object> extends React.PureComponent<AsyncPickerProps<T>, AsyncPickerState<T>> {
  private lastIssuedRequestTimestamp: number = 0;
  private chipPickerRef: React.RefObject<ChipPicker> = React.createRef();

  public static makeDebouncedFunction = (func: () => void, timeout: number): Function & Cancelable => {
    return debounce(func, timeout);
  };

  public static defaultProps: Pick<AsyncPickerProps<Object>, 'maxLines' | 'debounceTimeout' | 'label' | 'testId'> = {
    maxLines: 2,
    debounceTimeout: 200,
    label: '',
    testId: ''
  };

  public state: AsyncPickerState<T> = {
    recipientsSearchList: [],
    filterValue: '',
    inflightFetchRecipientCallbacks: new Set(),
  };

  public render(): JSX.Element {
    const {
      recipients,
      onRecipientAdd,
      onRecipientsChange,
      onFilterChanged,
      recipientToListItem,
      recipientToChip,
      recipientToStringId,
      fetchRecipientsCallback,
      debounceTimeout,
      testId,
      ...rest
    } = this.props;

    const finalTestId = `${testId}-${scopedTestId}`;

    const listItems = this.state.recipientsSearchList.map(recipientToListItem);

    return <ChipPicker listItems={listItems}
      style={styles.picker}
      testId={finalTestId}
      onFilterItems={this.handleFilterChanged}
      onValueAdd={this.handleValueAdd}
      onValuesChange={this.handleValuesChange}
      ref={this.chipPickerRef}
      className={finalTestId}
      {...rest}>
      {recipients.map(recipientToChip)}
    </ChipPicker>;
  }

  public componentDidMount(): void {
    this.fetchRecipients();
  }

  public componentWillUnmount(): void {
    this.debouncedSearchFunction.cancel?.();
    this.state.inflightFetchRecipientCallbacks.forEach((promise: CancelablePromise<T[]>) => { promise.cancel(); });
  }

  /**
   * A handler to the value add event in the chip picker. It maps to the OnRecipientAdd event to be consumed by external code
   * @param value - The recipient instance to be added
   * @param targetIndex - The index at which the recipient should be added
   */
  private handleValueAdd = (value: string, targetIndex: number) => {
    if (!this.props.onRecipientAdd) {
      return;
    }

    const predicate = (recipient: T) => this.props.recipientToStringId(recipient) === value;

    const existingIndex = this.props.recipients.findIndex(predicate);

    if (existingIndex < 0) {
      const recipient = this.state.recipientsSearchList.find(predicate);
      if (recipient) {
        this.props.onRecipientAdd({ ...recipient }, targetIndex);
      }
    } else {
      this.chipPickerRef.current!.focusChipAtIndex(existingIndex);
    }
  };

  /**
   * A handler to the values change event in the chip picker. It maps to the OnRecipientsChange event to be consumed by external code
   * @param selectedValues - The list of values of currently selected list items
   */
  private handleValuesChange = (selectedValues: string[]) => {
    if (!this.props.onRecipientsChange) {
      return;
    }

    const getter = this.props.recipientToStringId;

    // prepare a dictionary for fast lookup
    const userMap: { [key: string]: T } = {};
    this.props.recipients.forEach(r => userMap[getter(r)] = r);

    // map selected values to users
    const selectedUsers = selectedValues.map(value => ({ ...userMap[value] }));
    this.props.onRecipientsChange(selectedUsers);
  };

  /**
   * Updates the state with the new filter value and triggers the debounced search function
   * @param filter - The text typed by the user
   */
  private handleFilterChanged = (filter: string) => {
    this.setState({ filterValue: filter.trim() }, this.filterChangeStateChangeCallback);
  };

  /**
   * Internal callback of filter changes. Only exists to avoid passing an arrow function callback to setState
   */
  private filterChangeStateChangeCallback = () => {
    if (this.props.onFilterChanged) {
      this.props.onFilterChanged(this.state.filterValue);
    }
    this.debouncedSearchFunction();
  };

  /**
   * Asynchronously queries the server for a new page of recipients. Used in a debounced function
   */
  private fetchRecipients = (): void => {
    // get the current search term and timestamp
    const searchTerm = this.state.filterValue;
    const searchTimestamp = Date.now();
    this.lastIssuedRequestTimestamp = searchTimestamp;
    // fetch list of users. The fetch callback returns an array of promises that we should wait for
    const inflightPromise: Promise<T[]> = this.props.fetchRecipientsCallback(searchTerm);
    // While we wait, we'll make this callback "cancelable" and store it in state
    // If this component is unmounted before the promise resolves, we can reject it ourselves to prevent a memory leak.
    const inflightCanceleablePromise: CancelablePromise<T[]> = this.makeCancelable(inflightPromise);

    this.setState(
      (prevState) => {
        // Add the current in-flight promise to the list of in-flight promises
        return { inflightFetchRecipientCallbacks: new Set(prevState.inflightFetchRecipientCallbacks).add(inflightCanceleablePromise) };
      },
      () => {
        inflightCanceleablePromise.promise.then(newRecipientsList => {
          // If this search query is slow, it's possible that the user typed more and then a second search
          // query was issued and it has already completed. In that case, the second query had more recent
          // results, and these results should be dropped because they are outdated.
          if (searchTimestamp >= this.lastIssuedRequestTimestamp) {

            // Update the search list with the results and remove the promise from the list, as it is no longer in-flight
            this.setState((prevState) => {
              let updatedInflightCallbacks = new Set(prevState.inflightFetchRecipientCallbacks);
              updatedInflightCallbacks.delete(inflightCanceleablePromise);
              return {
                recipientsSearchList: newRecipientsList,
                inflightFetchRecipientCallbacks: updatedInflightCallbacks
              };
            });
          }
        })
          .catch();
      });
  };

  // Wrap the given promise in another promise, which we can cancel before it resolves.
  private makeCancelable = (promise: Promise<T[]>): CancelablePromise<T[]> => {
    let hasCanceled = false;

    const wrappedPromise = new Promise<T[]>((resolve, reject) => {
      promise.then(
        val => hasCanceled ? reject({ isCanceled: true }) : resolve(val),
        error => hasCanceled ? reject({ isCanceled: true }) : reject(error)
      );
    });

    return {
      promise: wrappedPromise,
      cancel(): void {
        hasCanceled = true;
      },
    };
  };

  private debouncedSearchFunction: Function & Cancelable =
    AsyncPicker.makeDebouncedFunction(this.fetchRecipients, this.props.debounceTimeout);
}

interface CancelablePromise<T> {
  promise: Promise<T>;
  cancel: () => void;
}
